import { FinCorrectionMethod, FinCorrectionReason, FinDocDiscount, FinDocument, FinLine, FinTaxLine } from './finances.model';
import { fmtMoney, roundCcy } from './finances-format';

/**
 * Corrective invoices (README WP9): the rules the page shares with its sample layer, ported from the mockup's block
 * `corrective invoices (README WP9)` (`computeCorrection`, `changeLines`, `correctable`, `openTarget`…). Pure: they
 * read documents and never change them. The server does the same arithmetic when a correction is issued; the page
 * uses these to show it live before it is sent.
 *
 * A sent invoice is never edited. A correction is a new document in its own series (R-2026-0001) that names the
 * invoice it corrects, says why, and carries either only the difference (the original stays and carries the corrected
 * balance) or every line as it should be (it replaces the original). Applying one is never a payment.
 */

export const FIN_CORRECTION_REASONS: readonly { key: FinCorrectionReason; label: string }[] = [
  { key: 'amount', label: 'A price or quantity was wrong' },
  { key: 'discount', label: 'A discount or price change after the invoice' },
  { key: 'returned', label: 'Work returned, not delivered or cancelled' },
  { key: 'tax', label: 'The tax was wrong' },
  { key: 'details', label: 'The client’s details were wrong' },
  { key: 'insolvent', label: 'The client is in insolvency proceedings' },
  { key: 'uncollectible', label: 'The debt can’t be collected' },
];

export const FIN_CORRECTION_METHODS: Record<FinCorrectionMethod, string> = {
  difference: 'Only the difference',
  replace: 'Replace the whole invoice',
};

export function reasonLabel(key: string): string {
  return FIN_CORRECTION_REASONS.find(r => r.key === key)?.label ?? key;
}

/** Rounded to cents, as the mockup's `r2c`. */
export function r2c(v: number): number {
  return Math.round(v * 100) / 100;
}

/** "1,250", "4.5": at most three decimals, thousands separated. */
export function num(v: number): string {
  const s = String(Math.round(v * 1000) / 1000);
  const p = s.split('.');
  return p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (p[1] ? '.' + p[1] : '');
}

/** "+$12.00", "−$15.20". */
export function signed(v: number, ccy: string): string {
  return (v > 0.004 ? '+' : '') + fmtMoney(v, ccy);
}

/** Unit prices such as $0.036 need three decimals to multiply back. */
function unitPrice(v: number, ccy: string): string {
  let s = fmtMoney(v, ccy);
  if (Math.abs(Math.round(v * 100) / 100 - v) > 0.0000001) s = s.replace(/\d[\d,]*\.\d\d$/, v.toFixed(3));
  return s;
}

/** "800 words × $0.16", or the amount of a flat line. */
export function describeLine(l: { qty: number; unit: string; unitPrice: number; amount: number }, ccy: string): string {
  return l.unit === 'flat' ? fmtMoney(l.amount, ccy) : num(l.qty) + ' ' + l.unit + ' × ' + unitPrice(l.unitPrice, ccy);
}

/** The invoice as it stands after earlier corrections by difference: what the next correction starts from. */
export function asNow(d: FinDocument): { lines: FinLine[]; subtotal: number; discount: FinDocDiscount | null; total: number } {
  return d.current ?? { lines: d.lines, subtotal: d.subtotal, discount: d.discount, total: d.total };
}

/** A line as it should be: quantity and rate (on a flat line the rate is its amount); NaN keeps the line's. */
export interface FinCorrRow {
  qty: number;
  rate: number;
}

/** One worked-out correction: the invoice as it should be, and the corrective invoice's own lines and totals. */
export interface FinComputedCorrection {
  rows: (FinLine & { was: FinLine })[];
  changed: boolean;
  lines: FinLine[];
  subtotal: number;
  discount: FinDocDiscount | null;
  taxLines: FinTaxLine[];
  total: number;
  before: { subtotal: number; discount: FinDocDiscount | null; total: number };
  after: { lines: FinLine[]; subtotal: number; discount: FinDocDiscount | null; total: number; taxLines: FinTaxLine[] };
}

/** A tax line as it should be (reason "the tax was wrong"): what it is; its amount is worked out on the corrected base. */
export interface FinTaxSpec {
  kind: 'tax' | 'withholding';
  code?: string;
  label: string;
  rate: number;
  baseRatio?: number | null;
}

/** Which tax a line is: the same kind, code, name and rate are the same tax (as the server matches them). */
function taxKey(t: { kind?: string; code?: string; label: string; rate: number }): string {
  return (t.kind ?? 'tax').toUpperCase() + '|' + (t.code ?? '').toUpperCase() + '|' + t.label.toUpperCase() + '|' + Number(t.rate);
}

/**
 * The invoice's tax as it stands: as its last correction by difference left it (`current.taxLines`), else its own
 * tax lines plus what each correction changed, by position (`docs` holds its correctives; an older answer without
 * `current.taxLines`). Invoices without tax lines (most of the sample data) have none.
 */
export function taxNow(docs: readonly FinDocument[], d: FinDocument): FinTaxLine[] {
  if (d.current?.taxLines) return d.current.taxLines.map(t => ({ ...t }));
  const out = (d.taxLines ?? []).map(t => ({ ...t }));
  for (const id of d.corrections ?? []) {
    const c = docs.find(x => x.id === id);
    if (!c || c.corrective?.method !== 'difference') continue;
    (c.taxLines ?? []).forEach((t, i) => {
      if (out[i]) out[i] = { ...out[i], amount: r2c(out[i].amount + t.amount), base: r2c((out[i].base ?? 0) + (t.base ?? 0)) };
    });
  }
  return out;
}

/**
 * The mockup's `computeCorrection`. `rows`: one per line of the invoice as it now stands. The discount percent is
 * worked out again on the corrected lines; tax keeps the original's rates on the corrected base. By difference the
 * corrective lists only the changed lines (same rate: the change of quantity; else one flat line of the change of
 * amount) and its total is after − before; a replacement repeats every line.
 */
export function computeCorrection(
  d: FinDocument, method: FinCorrectionMethod, rows: FinCorrRow[], tax: FinTaxLine[] = [], spec: FinTaxSpec[] | null = null,
): FinComputedCorrection {
  const base = asNow(d);
  const ccy = d.ccy;
  const disc = base.discount && base.discount.pct ? base.discount : null;
  const after = (base.lines ?? []).map((l, i) => {
    const r = rows[i] ?? { qty: NaN, rate: NaN };
    const flat = l.unit === 'flat';
    const qty = flat ? l.qty : isFinite(r.qty) ? Math.max(0, r.qty) : l.qty;
    const rate = isFinite(r.rate) ? Math.max(0, r.rate) : flat ? l.amount : l.unitPrice;
    return { desc: l.desc, qty, unit: l.unit, unitPrice: flat ? l.unitPrice : rate, amount: roundCcy(flat ? rate : qty * rate, ccy), was: l };
  });
  const sub = roundCcy(after.reduce((a, l) => a + l.amount, 0), ccy);
  const discAmt = disc ? roundCcy((sub * disc.pct) / 100, ccy) : 0;
  const beforeDisc = base.discount ? base.discount.amount : 0;
  const afterBase = sub - discAmt;
  // The tax on the corrected base: the invoice's own lines, each scaled from its own base (its rate and any part-base
  // share); or, when the tax was wrong, the lines as they should be (rate × base × share, withholding negative).
  const ofSpec = (l: FinTaxSpec) => {
    const amount = roundCcy((afterBase * (l.kind === 'withholding' ? 1 : l.baseRatio ?? 1) * l.rate) / 100, ccy);
    return l.kind === 'withholding' ? -amount : amount;
  };
  const taxAfter: FinTaxLine[] = spec
    ? spec.map(l => ({ kind: l.kind, code: l.code, label: l.label, rate: l.rate, baseRatio: l.baseRatio ?? 1, base: afterBase, amount: ofSpec(l) }))
    : tax.map(t => ({
      ...t, base: afterBase,
      amount: t.base ? roundCcy((t.amount * afterBase) / t.base, ccy) : roundCcy(((t.kind === 'withholding' ? -1 : 1) * afterBase * t.rate) / 100, ccy),
    }));
  const total = roundCcy(afterBase + taxAfter.reduce((a, x) => a + x.amount, 0), ccy);
  // The corrective's own tax: every line when it replaces; by difference the change line by line when the rates are
  // kept, or by tax (kind, code, name, rate) when they changed: a removed rate goes to −, a new one is added.
  let taxLines: FinTaxLine[];
  if (method === 'replace') taxLines = taxAfter;
  else if (!spec) taxLines = taxAfter.map((a, i) => ({ ...tax[i], base: r2c(afterBase - (tax[i].base ?? 0)), amount: r2c(a.amount - tax[i].amount) }));
  else {
    const before = new Map(tax.map(t => [taxKey(t), t] as const));
    const afterBy = new Map(taxAfter.map(t => [taxKey(t), t] as const));
    const keys = [...before.keys(), ...[...afterBy.keys()].filter(k => !before.has(k))];
    taxLines = keys.map(k => {
      const b = before.get(k);
      const a = afterBy.get(k);
      return { ...(a ?? b)!, base: r2c((a?.base ?? 0) - (b?.base ?? 0)), amount: r2c((a?.amount ?? 0) - (b?.amount ?? 0)) };
    }).filter(t => Math.abs(t.amount) > 0.004 || Math.abs(t.base ?? 0) > 0.004);
  }
  const moved = (l: { amount: number; was: FinLine }) => Math.abs(l.amount - l.was.amount) > 0.004;
  const changed = after.some(moved) || (!!spec && method !== 'replace' && taxLines.some(t => Math.abs(t.amount) > 0.004));
  let lines: FinLine[];
  if (method === 'replace') lines = after.map(l => ({ desc: l.desc, qty: l.qty, unit: l.unit, unitPrice: l.unitPrice, amount: l.amount }));
  else {
    lines = after.filter(moved).map(l => {
      const w = l.was;
      const sameRate = l.unit !== 'flat' && Math.abs(l.unitPrice - w.unitPrice) < 0.0000001;
      if (sameRate) return { desc: w.desc, sub: num(w.qty) + ' → ' + num(l.qty) + ' ' + w.unit, qty: r2c(l.qty - w.qty), unit: w.unit, unitPrice: w.unitPrice, amount: r2c(l.amount - w.amount) };
      return { desc: w.desc, sub: 'Was ' + describeLine(w, ccy) + '; now ' + describeLine(l, ccy), qty: 1, unit: 'flat', unitPrice: r2c(l.amount - w.amount), amount: r2c(l.amount - w.amount) };
    });
  }
  const subD = method === 'replace' ? sub : r2c(lines.reduce((a, l) => a + l.amount, 0));
  const discD = method === 'replace' ? discAmt : r2c(discAmt - beforeDisc);
  return {
    rows: after,
    changed,
    lines,
    subtotal: subD,
    discount: disc ? { pct: disc.pct, reason: disc.reason, amount: discD } : null,
    taxLines,
    total: method === 'replace' ? total : r2c(total - base.total),
    before: { subtotal: base.subtotal, discount: base.discount, total: base.total },
    after: {
      lines: after.map(l => ({ desc: l.desc, qty: l.qty, unit: l.unit, unitPrice: l.unitPrice, amount: l.amount })),
      subtotal: sub,
      discount: disc ? { pct: disc.pct, reason: disc.reason, amount: discAmt } : null,
      total,
      taxLines: taxAfter,
    },
  };
}

/** What the printed corrective invoice lists under "What this corrects". */
export function changeLines(d: FinDocument, x: FinComputedCorrection): string[] {
  const out: string[] = [];
  for (const l of x.rows) {
    if (Math.abs(l.amount - l.was.amount) > 0.004) {
      out.push(l.desc + ': ' + describeLine(l.was, d.ccy) + ' → ' + describeLine(l, d.ccy) + ' (' + signed(r2c(l.amount - l.was.amount), d.ccy) + ')');
    }
  }
  if (x.before.discount && x.after.discount && Math.abs(x.before.discount.amount - x.after.discount.amount) > 0.004) {
    out.push('Discount ' + x.after.discount.pct + '%: ' + fmtMoney(-x.before.discount.amount, d.ccy) + ' → ' + fmtMoney(-x.after.discount.amount, d.ccy));
  }
  return out;
}

/** The rows that keep every line as it is (the panel's starting values). */
export function baseRows(d: FinDocument): FinCorrRow[] {
  return (asNow(d).lines ?? []).map(l => ({ qty: l.qty, rate: l.unit === 'flat' ? l.amount : l.unitPrice }));
}

/** "Cancel invoice": every quantity 0, every flat amount 0. */
export function zeroRows(d: FinDocument): FinCorrRow[] {
  return (asNow(d).lines ?? []).map(l => ({ qty: 0, rate: l.unit === 'flat' ? 0 : l.unitPrice }));
}

/**
 * A sent invoice that can be corrected: not a draft, cancelled or replaced, not closed by a verb this visit, and not
 * a corrective that takes money off (that one is corrected through the invoice it corrects).
 */
export function correctable(d: FinDocument, closedThisVisit: boolean): boolean {
  if (d.kind !== 'invoice' || d.state === 'draft' || d.state === 'cancelled' || d.replacedBy || d.cancelledBy || closedThisVisit) return false;
  return !(d.corrective && d.total < 0);
}

/** Replace is offered only while the invoice has no earlier correction. */
export function canReplace(d: FinDocument): boolean {
  return !(d.corrections && d.corrections.length);
}

/**
 * Where money owed to a client can go instead of a refund: their invoice in the same currency that is due first and
 * still has something to be paid (`open` = what is still to be paid on a document).
 */
export function openTarget(docs: readonly FinDocument[], d: FinDocument, open: (x: FinDocument) => number, closed: (x: FinDocument) => boolean = () => false): FinDocument | null {
  return docs
    .filter(x => x.kind === 'invoice' && x.id !== d.id && x.personId === d.personId && x.ccy === d.ccy && x.state !== 'draft' && x.state !== 'cancelled'
      && !x.replacedBy && !x.cancelledBy && !closed(x) && open(x) > 0.004)
    .sort((a, b) => (a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : a.id < b.id ? -1 : 1))[0] ?? null;
}

/**
 * The next number in the corrective series for the year (`R-2026-0006`), for the panel's "Issue R-… and send" and
 * the cancel sentence. The server gives the real one when it is issued.
 */
export function nextCorrectiveNumber(docs: readonly FinDocument[], today: string): string {
  const year = today.slice(0, 4);
  const re = new RegExp('^R-' + year + '-(\\d{4})$');
  let max = 0;
  for (const x of docs) {
    const m = re.exec(x.id);
    if (m) max = Math.max(max, +m[1]);
  }
  return 'R-' + year + '-' + String(max + 1).padStart(4, '0');
}

/** Where a document sits in the list: a correction by difference under the invoice it corrects; a replaced invoice under its replacement. */
export function parentOf(d: FinDocument): string | null {
  if (d.corrective && d.corrective.method !== 'replace') return d.corrective.of;
  if (d.replacedBy) return d.replacedBy;
  return null;
}

/** "Corrects INV-…", "Replaced by R-…", "Cancelled by R-…", "Corrected by R-…, R-…"; '' when none. */
export function relText(d: FinDocument): string {
  if (d.corrective) return (d.corrective.method === 'replace' ? 'Replaces ' : 'Corrects ') + d.corrective.of;
  if (d.replacedBy) return 'Replaced by ' + d.replacedBy;
  if (d.cancelledBy) return 'Cancelled by ' + d.cancelledBy;
  if (d.corrections && d.corrections.length) return 'Corrected by ' + d.corrections.join(', ');
  return '';
}
